import {
    useMemo, useRef, useState, useCallback,
} from 'react';
import { useOutsideClick, useWindowResize } from '@rainbow-modules/hooks';
import { InternalOverlay, RenderIf } from 'react-rainbow-components';
import PhoneIcon from 'components/icons/phone';
import ActiveCallsOverlay from './ActiveCallsOverlay';
import {
    NotificationMenuContainer, StyledButtonIcon,
    StyledBadgeOverlay,
    StyledSpinner,
} from './styled';
import { ActiveCall } from './types';

interface ActivePhoneCallButtonProps {
    activeCallsCount?: number;
    isLoading?: boolean;
    onClick?: () => void;
    onViewCall?: (callId: string) => void;
    onClickCall?: (call: ActiveCall) => void;
    className?: string;
    activeCalls?: ActiveCall[];
}

const ActivePhoneCallButton = ({
    activeCallsCount,
    onClick = () => {},
    onViewCall = () => {},
    onClickCall = () => {},
    className,
    isLoading = false,
    activeCalls = [],
}: ActivePhoneCallButtonProps) => {
    const buttonRef = useRef<any>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    // Use provided count or derive from calls array
    const callsCount = activeCallsCount ?? activeCalls.length;

    useWindowResize(() => setIsOpen(false), isOpen);
    useOutsideClick(dropdownRef, () => setIsOpen(false), isOpen);

    const toggle = useCallback(() => {
        setIsOpen(!isOpen);
        onClick();
    }, [isOpen, onClick]);

    const value = useMemo(
        () => {
            if (typeof callsCount === 'number' && callsCount > 0) {
                return callsCount > 99 ? '99+' : callsCount;
            }
            return undefined;
        },
        [callsCount],
    );

    const icon = (
        <StyledBadgeOverlay
            variant="brand"
            overlap="circle"
            isHidden={!callsCount}
            value={value}
        >
            <PhoneIcon title="Active Phone Calls" />
        </StyledBadgeOverlay>
    );

    return (
        <NotificationMenuContainer className={className}>
            <RenderIf isTrue={isLoading}>
                <StyledSpinner type="arc" variant="brand">
                    <StyledButtonIcon icon={icon} />
                </StyledSpinner>
            </RenderIf>
            <RenderIf isTrue={!isLoading}>
                <StyledButtonIcon
                    ref={buttonRef}
                    icon={icon}
                    onClick={toggle}
                />
                <InternalOverlay
                    isVisible={isOpen}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    triggerElementRef={() => buttonRef.current?.htmlElementRef}
                >
                    <ActiveCallsOverlay
                        ref={dropdownRef}
                        calls={activeCalls}
                        onCallClick={onClickCall}
                        onViewCall={onViewCall}
                    />
                </InternalOverlay>
            </RenderIf>
        </NotificationMenuContainer>
    );
};

export default ActivePhoneCallButton;
